/**
 * RLQ — LobbyEntryInternal
 *
 * Staff-facing "add a visitor to the lobby" form. Used when an FSR or Lobby
 * Officer greets a walk-in at the door and types in the basics for them.
 *
 * Audience: any provisioned staff (FSR+).
 *
 * Flow (matches the kiosk where it makes sense, but compressed for staff):
 *   1. Visitor block — name OR existing-CIF lookup hint.
 *   2. Reason — same `ServiceTypePicker` the kiosk uses.
 *   3. Submit → creates a lobby session via `client.retail.sessions.create()`
 *      with `origin: 'in-branch'` and routes back to the queue.
 *
 * No raw GUIDs are surfaced anywhere on this page.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router';
import { CheckCircle2, Search, UserPlus, Users } from '@retail-ops/panel-shell/fi';

import { Button, FilterChips, Pill } from '@/components/primitives';
import { Toast, type ToastVariant } from '@/components/common/Toast';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { asIso8601 } from '@retail-ops/shared-types';
import type { SessionEventPayload } from '@retail-ops/shared-types';

import { ServiceTypePicker, useServiceTypes } from '../components/ServiceTypePicker';

type CustomerKind = 'existing' | 'new';

interface SubmitState {
  submitting: boolean;
  error: string | null;
  okMessage: string | null;
}

export default function LobbyEntryInternal(): JSX.Element {
  const client = useApiClient();
  const { user } = useAuth();
  const navigate = useNavigate();
  const services = useServiceTypes();

  const [kind, setKind] = useState<CustomerKind>('existing');
  const [identifier, setIdentifier] = useState<string>('');
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [serviceCode, setServiceCode] = useState<string | null>(null);
  const [submit, setSubmit] = useState<SubmitState>({
    submitting: false,
    error: null,
    okMessage: null,
  });

  const selectedService = services.find((s) => s.code === serviceCode) ?? null;

  const customerOk =
    kind === 'existing'
      ? identifier.trim().length >= 4
      : firstName.trim().length > 0 && lastName.trim().length > 0;
  const canSubmit = !submit.submitting && customerOk && !!selectedService;

  const handleSubmit = async (ev: React.FormEvent<HTMLFormElement>): Promise<void> => {
    ev.preventDefault();
    if (!canSubmit || !selectedService) return;

    setSubmit({ submitting: true, error: null, okMessage: null });

    try {
      const res = await client.retail.sessions.create({
        officerCode: user?.officerCode ?? user?.upn ?? 'STAFF',
        ...(user?.upn ? { officerEmail: user.upn } : {}),
        ...(user?.displayName ? { officerName: user.displayName } : {}),
        branch: user?.activeBranchCode ?? user?.branchCode ?? '1',
        sessionType: 'lobby',
        serviceType: selectedService.code,
        origin: 'in-branch',
      });

      const sessionId = res.session?.sessionId;

      if (sessionId) {
        const payload: SessionEventPayload = {
          sessionId,
          eventType: 'queue-entry-created',
          eventCategory: 'session',
          severity: 'info',
          actor: user?.upn ?? 'staff',
          timestamp: asIso8601(new Date().toISOString()),
          payloadJson: JSON.stringify({
            entryChannel: 'internal',
            serviceType: selectedService.code,
            requiredPermission: selectedService.requiredPermission,
            targetModule: selectedService.targetModule,
            visitor:
              kind === 'existing'
                ? { kind: 'existing', maskedIdentifier: maskIdentifier(identifier) }
                : { kind: 'new', firstName, lastName, phone: phone || undefined },
          }),
        };
        client.retail.events.write(payload).catch(() => {
          // non-blocking
        });
      }

      const visitorLabel =
        kind === 'existing'
          ? 'visitor'
          : `${firstName.trim()} ${lastName.trim()}`.trim() || 'visitor';

      setSubmit({
        submitting: false,
        error: null,
        okMessage: `Added ${visitorLabel} to the lobby. Returning to the queue…`,
      });

      // Brief confirmation, then back to the queue.
      window.setTimeout(() => navigate('/rlq/queue'), 900);
    } catch (err: unknown) {
      setSubmit({
        submitting: false,
        error: err instanceof Error ? err.message : 'Could not add to lobby.',
        okMessage: null,
      });
    }
  };

  const toastVariant: ToastVariant | null = submit.error
    ? 'err'
    : submit.okMessage
      ? 'ok'
      : null;
  const toastMessage = submit.error ?? submit.okMessage ?? '';

  return (
    <div className="lobby-page">
      <header className="lobby-page__header">
        <div className="lobby-page__title-row">
          <h1 className="lobby-page__title">Add visitor</h1>
          <Pill variant="muted">Internal entry</Pill>
        </div>
        <p className="lobby-page__subtitle">
          Greet a walk-in at the door. We'll create a lobby entry and pre-route
          them to the right module on claim.
        </p>
      </header>

      <form className="lobby-form" onSubmit={handleSubmit}>
        <section className="lobby-form__section">
          <h2 className="lobby-form__section-title">Who's visiting?</h2>
          <FilterChips
            label="Customer status"
            options={[
              { value: 'existing', label: 'Existing customer' },
              { value: 'new', label: 'New / prospect' },
            ]}
            selected={kind}
            onChange={(v) => setKind(v as CustomerKind)}
          />

          {kind === 'existing' ? (
            <label className="lobby-form__field">
              <span className="lobby-form__field-label">
                Last 4 of SSN, phone, or account
              </span>
              <span className="lobby-form__field-control">
                <Search size={14} aria-hidden />
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="e.g. 4421"
                  maxLength={16}
                />
              </span>
              <span className="lobby-form__field-helper">
                We'll mask this in the audit log — only the last 4 are stored.
              </span>
            </label>
          ) : (
            <div className="lobby-form__row">
              <label className="lobby-form__field">
                <span className="lobby-form__field-label">First name</span>
                <span className="lobby-form__field-control">
                  <input
                    type="text"
                    autoComplete="given-name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    maxLength={40}
                  />
                </span>
              </label>
              <label className="lobby-form__field">
                <span className="lobby-form__field-label">Last name</span>
                <span className="lobby-form__field-control">
                  <input
                    type="text"
                    autoComplete="family-name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    maxLength={40}
                  />
                </span>
              </label>
              <label className="lobby-form__field">
                <span className="lobby-form__field-label">Phone (optional)</span>
                <span className="lobby-form__field-control">
                  <input
                    type="tel"
                    autoComplete="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    maxLength={20}
                    placeholder="(573) 555-1212"
                  />
                </span>
              </label>
            </div>
          )}
        </section>

        <section className="lobby-form__section">
          <h2 className="lobby-form__section-title">What do they need?</h2>
          <ServiceTypePicker
            selected={serviceCode}
            onChange={(code) => setServiceCode(code)}
          />
        </section>

        <div className="lobby-form__actions">
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate('/rlq/queue')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!canSubmit}
            startIcon={kind === 'new' ? <UserPlus size={14} /> : <Users size={14} />}
          >
            {submit.submitting ? 'Adding…' : 'Add to lobby'}
          </Button>
        </div>
      </form>

      {toastVariant && (
        <div className="lobby-toast-anchor">
          <Toast
            variant={toastVariant}
            message={toastMessage}
            autoHideMs={3000}
            onClose={() =>
              setSubmit((s) => ({ ...s, error: null, okMessage: null }))
            }
          />
        </div>
      )}

      {submit.okMessage && (
        <div className="lobby-form__success">
          <CheckCircle2 size={14} aria-hidden /> {submit.okMessage}
        </div>
      )}
    </div>
  );
}

function maskIdentifier(input: string): string {
  const clean = input.trim();
  if (clean.length <= 4) return `****${clean}`;
  return `${'*'.repeat(Math.max(0, clean.length - 4))}${clean.slice(-4)}`;
}
